import { addToast } from '@app/components/various/Toasts';
import { setHomepageSlug } from '@app/services/homepageRepository';
import { editNavigationItems } from '@app/services/settingsRepository';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, SettingsNavigationItemsDTO } from '@typings';
import { useTranslation } from 'react-i18next';

import { HOMEPAGE_QUERY_KEY } from '../pages/useHomepage';
import { NAVIGATION_ITEMS_QUERY_KEY } from '../pages/useNavigationItems';

import { SETTINGS_QUERY_KEY } from './useSettings';

type MutationVariables = Settings.NavigationFormFields & SettingsNavigationItemsDTO;

export const useEditNavigationSettings = () => {
  const { t } = useTranslation(['cms', 'settings']);
  const queryClient = useQueryClient();
  const mutationFn = async (values: MutationVariables) => {
    const { navigationItems, slug } = values;

    const editNavigationMutation = editNavigationItems({
      navigationItems,
    }).catch(() => {
      throw new Error(t('cms:update_navigation_fail'));
    });

    const editHompageMutation = setHomepageSlug(slug).catch(() => {
      throw new Error(t('settings:company_logo_update_failure'));
    });

    const results = await Promise.allSettled([editHompageMutation, editNavigationMutation]);

    const [editResult] = results;

    if (editResult.status === 'fulfilled') {
      return {
        errors: results.map(result => result.status === 'rejected' && result.reason.message).filter(Boolean),
        values: editResult.value,
      };
    }

    throw new Error(t('settings:customisation_update_fail'));
  };

  return useMutation({
    mutationFn,
    onSuccess: () => {
      addToast(t('settings:navigation_updated'), { type: 'success' });
      queryClient.invalidateQueries({ queryKey: [HOMEPAGE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [NAVIGATION_ITEMS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SETTINGS_QUERY_KEY] });
    },
  });
};
